import { getCsrfToken } from 'constants/api/FetchUtils';
import { createSignature } from 'utils/CreatePostSignatureUtil';
import getJwtToken from 'utils/GetJwtToken';
import type { SendResult, TrackEvent } from './types';

const TAG = 'Tracking';

const API_BASE_URL =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_PROD_API_URL
    : process.env.REACT_APP_DEV_API_URL;

const APP_API_KEY =
  process.env.REACT_APP_ENVIRONMENT === 'production'
    ? process.env.REACT_APP_PROD_API_KEY
    : process.env.REACT_APP_DEV_API_KEY;

const OFFLINE: SendResult = { ok: false, status: 0, enabled: true };
const SIGNED_OUT: SendResult = { ok: false, status: 401, enabled: true };

// The CSRF endpoint has no expiry field; the token is a 1 hour JWT. The regular API client
// fetches a fresh one for every request, which a tracker cannot afford (it would double its
// traffic, and it cannot wait for a round trip while the page is unloading). Cache it.
const CSRF_TTL_MS = 50 * 60 * 1000;
let csrf: { token: string; at: number } | null = null;
let csrfPending: Promise<string> | null = null;

const cachedCsrf = (): string | null =>
  csrf !== null && Date.now() - csrf.at < CSRF_TTL_MS ? csrf.token : null;

async function loadCsrf(): Promise<string> {
  const cached = cachedCsrf();
  if (cached) return cached;
  if (csrfPending === null) {
    csrfPending = getCsrfToken(TAG)
      .then((token) => {
        csrf = { token, at: Date.now() };
        return token;
      })
      .finally(() => {
        csrfPending = null;
      });
  }
  return csrfPending;
}

/** Fetch the CSRF token ahead of time so the final flush at hand-off never waits for it. */
export function warmCsrfToken(): void {
  if (cachedCsrf() === null) loadCsrf().catch(() => undefined);
}

/**
 * POSTs one batch to `<API base>/track`.
 *
 * `fetch` with `keepalive`, not `sendBeacon`: the endpoint needs the API key, the CSRF token,
 * the user's JWT and the body signature as headers, and a beacon cannot set headers.
 *
 * The JWT and the signature are computed before the first await, so a logout that clears the
 * token right after asking for the final flush does not empty the request.
 */
export async function sendTrackBatch(
  events: TrackEvent[],
  options: { keepalive: boolean },
): Promise<SendResult> {
  const jwt = getJwtToken();
  if (!jwt) return SIGNED_OUT;

  const payload = { events };
  const signature = createSignature(payload, TAG);
  const body = JSON.stringify(payload);

  try {
    const csrfToken = cachedCsrf() ?? (await loadCsrf());

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${APP_API_KEY || ''}`,
      'x-csrf-token': csrfToken,
      'x-jwt-token': jwt,
    };
    if (signature) headers['X-Signature'] = signature;

    const response = await fetch(`${API_BASE_URL}/track`, {
      method: 'POST',
      headers,
      body,
      keepalive: options.keepalive,
      credentials: 'include',
    });
    if (response.status === 403) csrf = null;

    let enabled = true;
    if (response.ok) {
      try {
        const json = (await response.json()) as { data?: { enabled?: boolean } };
        enabled = json.data?.enabled !== false;
      } catch {
        // An unreadable body still means the batch was accepted.
      }
    }
    return { ok: response.ok, status: response.status, enabled };
  } catch {
    return OFFLINE;
  }
}
